import React, { createContext, useContext, useEffect, ReactNode, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { getCurrentUser, refreshTokens, clearAuth } from '../store/slices/authSlice';
import { User } from '../types/auth';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  const { user, isAuthenticated, isLoading, error, token } = useAppSelector((state) => state.auth);
  const hasInitialized = useRef(false);

  // Initialize authentication on app startup
  useEffect(() => {
    const initializeAuth = async () => {
      // Prevent repeated initialization attempts
      if (hasInitialized.current) return;
      hasInitialized.current = true;

      // Check if we have a token
      const storedToken = localStorage.getItem('token');
      const storedRefreshToken = localStorage.getItem('refreshToken');

      if (storedToken && storedRefreshToken) {
        try {
          // Try to get current user to verify token is still valid
          await dispatch(getCurrentUser()).unwrap();
        } catch (error: any) {
          // Check if it's a network error or auth error
          const isAuthError = error?.status === 401 || error?.status === 403;

          if (isAuthError) {
            // If it's an auth error, try to refresh the token
            try {
              await dispatch(refreshTokens()).unwrap();
              // After successful refresh, get current user
              await dispatch(getCurrentUser()).unwrap();
            } catch (refreshError: any) {
              // Only clear auth if refresh also returns auth error
              const isRefreshAuthError = refreshError?.status === 401 || refreshError?.status === 403;
              if (isRefreshAuthError) {
                dispatch(clearAuth());
              }
              // For network errors during refresh, keep existing state
            }
          }
          // For network errors during initial getCurrentUser, keep existing auth state
        }
      } else if (isAuthenticated) {
        // If marked as authenticated but no tokens, clear state
        dispatch(clearAuth());
      }
    };

    // Only initialize if we have tokens or if state says authenticated but we don't have user data
    const storedToken = localStorage.getItem('token');
    const shouldInitialize = storedToken || (isAuthenticated && !user);

    if (shouldInitialize && !hasInitialized.current) {
      initializeAuth();
    }
  }, [dispatch, user, isAuthenticated]);

  // Auto-refresh token before it expires
  useEffect(() => {
    if (!isAuthenticated || !token) return;

    // Set up token refresh interval (refresh every 20 hours for 24-hour tokens)
    const refreshInterval = setInterval(async () => {
      try {
        await dispatch(refreshTokens()).unwrap();
      } catch (error) {
        // If refresh fails, clear auth state
        dispatch(clearAuth());
      }
    }, 20 * 60 * 60 * 1000); // 20 hours

    return () => clearInterval(refreshInterval);
  }, [dispatch, isAuthenticated, token]);

  const login = async (username: string, password: string): Promise<void> => {
    const { login: loginAction } = await import('../store/slices/authSlice');
    await dispatch(loginAction({ username_or_email: username, password })).unwrap();
  };

  const logout = async () => {
    const { logout: logoutAction } = await import('../store/slices/authSlice');
    dispatch(logoutAction());
    dispatch(clearAuth());
  };

  const handleClearError = async () => {
    const { clearError } = await import('../store/slices/authSlice');
    dispatch(clearError());
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
    clearError: handleClearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};